import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Crown, Star, UserCircle, Users, Mail } from 'lucide-react';
import { productClientsService } from '../../services/product-clients.service';
import { leadsService } from '../../services/leads.service';
import {
  PLAYBOOK_GROUP_LABELS,
  PERSONA_LABELS,
  THEME_LABELS,
} from '../../features/pipeline/playbookLabels';
import { useProductPlans } from '../../hooks/useProductPlans';

// ============================================
// Types & Constants
// ============================================

type Plan = 'ENTERPRISE' | 'PRO' | 'FREE';

const PLAN_BADGE_CLASSES: Record<Plan, string> = {
  ENTERPRISE: 'bg-purple-100 text-purple-700',
  PRO: 'bg-blue-100 text-blue-700',
  FREE: 'bg-gray-100 text-gray-600',
};

function PlanIcon({ plan }: { plan: Plan }) {
  if (plan === 'ENTERPRISE') {
    return <Crown className="w-3.5 h-3.5" />;
  }
  if (plan === 'PRO') {
    return <Star className="w-3.5 h-3.5" />;
  }
  return <UserCircle className="w-3.5 h-3.5" />;
}

// ============================================
// Props
// ============================================

interface RecipientSelectorProps {
  productId: number;
  selectedClients: number[];
  selectedLeads: number[];
  onClientsChange: (ids: number[]) => void;
  onLeadsChange: (ids: number[]) => void;
}

// ============================================
// Component
// ============================================

export function RecipientSelector({
  productId,
  selectedClients,
  selectedLeads,
  onClientsChange,
  onLeadsChange,
}: RecipientSelectorProps) {
  const [activeTab, setActiveTab] = useState<'clients' | 'leads'>('clients');
  const [clientSearch, setClientSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<number | ''>('');
  const [stageFilter, setStageFilter] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [leadStageFilter, setLeadStageFilter] = useState('');
  const [leadGroupFilter, setLeadGroupFilter] = useState('');
  const [leadPersonaFilter, setLeadPersonaFilter] = useState('');
  const [leadThemeFilter, setLeadThemeFilter] = useState('');

  const { data: plans = [] } = useProductPlans(productId);

  // Clients query
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: [
      'product-clients',
      productId,
      clientSearch,
      planFilter,
      stageFilter,
      'exclude-optout',
    ],
    queryFn: () =>
      productClientsService.list({
        productId,
        search: clientSearch || undefined,
        planId: planFilter === '' ? undefined : planFilter,
        stage: stageFilter || undefined,
        limit: 100,
        optOut: 'exclude',
      }),
    enabled: !!productId,
    placeholderData: (prev) => prev,
  });

  // Filtros compartilhados pela lista e pela contagem do controle
  const leadFilters = {
    productId,
    search: leadSearch || undefined,
    lifecycleStage: leadStageFilter || undefined,
    playbookGroup: leadGroupFilter || undefined,
    jevPersona: leadPersonaFilter || undefined,
    jevTheme: leadThemeFilter || undefined,
    optOut: 'exclude',
  };

  // Leads query
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', 'recipient-selector', leadFilters, 'exclude-holdout'],
    queryFn: () =>
      leadsService.list({ ...leadFilters, holdout: 'exclude', sort: 'priority', limit: 100 }),
    enabled: !!productId,
    placeholderData: (prev) => prev,
  });

  const { data: holdoutData } = useQuery({
    queryKey: ['leads', 'recipient-selector-holdout', leadFilters],
    queryFn: () => leadsService.list({ ...leadFilters, holdout: 'only', limit: 1 }),
    enabled: !!productId,
  });
  const holdoutCount: number = holdoutData?.total ?? 0;

  const clients: Record<string, unknown>[] = clientsData?.data || clientsData?.clients || [];
  const allLeads: Record<string, unknown>[] = Array.isArray(leadsData)
    ? leadsData
    : leadsData?.data || leadsData?.leads || [];
  const leads = allLeads.filter((l) => !!l.email);

  // Client handlers
  const toggleClient = (id: number) => {
    if (selectedClients.includes(id)) {
      onClientsChange(selectedClients.filter((c) => c !== id));
    } else {
      onClientsChange([...selectedClients, id]);
    }
  };

  const selectAllClients = () => {
    const allIds = clients.map((c) => c.id as number);
    onClientsChange(allIds);
  };

  const clearAllClients = () => {
    onClientsChange([]);
  };

  // Lead handlers
  const toggleLead = (id: number) => {
    if (selectedLeads.includes(id)) {
      onLeadsChange(selectedLeads.filter((l) => l !== id));
    } else {
      onLeadsChange([...selectedLeads, id]);
    }
  };

  const totalSelected = selectedClients.length + selectedLeads.length;

  return (
    <div className="space-y-4">
      {/* Selected count summary */}
      {totalSelected > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
          <Mail className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>{totalSelected}</strong> destinatário{totalSelected !== 1 ? 's' : ''}{' '}
            selecionado{totalSelected !== 1 ? 's' : ''}
            {selectedClients.length > 0 &&
              ` (${selectedClients.length} cliente${selectedClients.length !== 1 ? 's' : ''})`}
            {selectedLeads.length > 0 &&
              ` (${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''})`}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-secondary-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'clients'
              ? 'bg-white text-secondary-900 shadow-sm'
              : 'text-secondary-600 hover:text-secondary-800'
          }`}
        >
          Clientes Docugen
          {selectedClients.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-600 text-white text-[10px] font-bold">
              {selectedClients.length > 9 ? '9+' : selectedClients.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('leads')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'leads'
              ? 'bg-white text-secondary-900 shadow-sm'
              : 'text-secondary-600 hover:text-secondary-800'
          }`}
        >
          Leads
          {selectedLeads.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-600 text-white text-[10px] font-bold">
              {selectedLeads.length > 9 ? '9+' : selectedLeads.length}
            </span>
          )}
        </button>
      </div>

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Buscar por nome ou e-mail..."
                className="input pl-9 w-full text-sm"
              />
            </div>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value === '' ? '' : Number(e.target.value))}
              className="input w-auto text-sm"
            >
              <option value="">Todos os planos</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="">Todas as categorias</option>
              <option value="NEW_ACCOUNT">Conta criada</option>
              <option value="FIRST_DOCUMENT">Primeiro documento</option>
              <option value="ACTIVE_USER">Usuário ativo</option>
              <option value="TRIED_LOCKED">Tentou premium</option>
              <option value="HEAVY_USAGE">Uso intenso</option>
              <option value="REACHED_LIMIT">Atingiu limite</option>
              <option value="INACTIVE">Inativo</option>
            </select>
            {clients.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAllClients}
                  className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  Selecionar todos ({clients.length})
                </button>
                {selectedClients.length > 0 && (
                  <button
                    onClick={clearAllClients}
                    className="px-3 py-1.5 text-xs font-medium text-secondary-600 bg-white border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
                  >
                    Limpar
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Client List */}
          <div className="border border-secondary-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
            {clientsLoading ? (
              <div className="flex items-center justify-center py-10 text-secondary-400 text-sm">
                Carregando clientes...
              </div>
            ) : clients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-secondary-400">
                <Users className="w-8 h-8" />
                <p className="text-sm">Nenhum cliente encontrado</p>
              </div>
            ) : (
              <ul className="divide-y divide-secondary-100">
                {clients.map((client) => {
                  const id = client.id as number;
                  const planObj = client.plan as { slug?: string; name?: string } | null;
                  const planSlug = planObj?.slug;
                  const isKnownPlan =
                    planSlug === 'ENTERPRISE' || planSlug === 'PRO' || planSlug === 'FREE';
                  const badgeClass = isKnownPlan
                    ? PLAN_BADGE_CLASSES[planSlug as Plan]
                    : 'bg-gray-100 text-gray-600';
                  const planLabel = planObj?.name ?? '—';
                  const isChecked = selectedClients.includes(id);

                  return (
                    <li
                      key={id}
                      onClick={() => toggleClient(id)}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isChecked ? 'bg-primary-50' : 'hover:bg-secondary-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleClient(id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-secondary-300 text-primary-600 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">
                          {(client.name as string) || '—'}
                        </p>
                        <p className="text-xs text-secondary-500 truncate">
                          {client.email as string}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${badgeClass}`}
                      >
                        {isKnownPlan && <PlanIcon plan={planSlug as Plan} />}
                        {planLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div className="space-y-3">
          {/* Lead search + lifecycle filter */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                placeholder="Buscar por nome, e-mail ou empresa..."
                className="input pl-9 w-full text-sm"
              />
            </div>
            <select
              value={leadStageFilter}
              onChange={(e) => setLeadStageFilter(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="">Todas as categorias</option>
              <option value="UNCATEGORIZED">Sem categoria</option>
              <option value="NEW_ACCOUNT">Conta criada</option>
              <option value="FIRST_DOCUMENT">Primeiro documento</option>
              <option value="ACTIVE_USER">Usuário ativo</option>
              <option value="TRIED_LOCKED">Tentou premium</option>
              <option value="HEAVY_USAGE">Uso intenso</option>
              <option value="REACHED_LIMIT">Atingiu limite</option>
              <option value="INACTIVE">Inativo</option>
            </select>
            <select
              value={leadGroupFilter}
              onChange={(e) => setLeadGroupFilter(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="">Todos os grupos</option>
              {Object.entries(PLAYBOOK_GROUP_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={leadPersonaFilter}
              onChange={(e) => setLeadPersonaFilter(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="">Todos os perfis</option>
              {Object.entries(PERSONA_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={leadThemeFilter}
              onChange={(e) => setLeadThemeFilter(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="">Todos os temas</option>
              {Object.entries(THEME_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {holdoutCount > 0 && (
            <p className="text-xs text-ink-tertiary">
              {holdoutCount}{' '}
              {holdoutCount === 1
                ? 'lead do grupo de controle ficou'
                : 'leads do grupo de controle ficaram'}{' '}
              de fora, para medir o efeito da campanha.
            </p>
          )}
          <div className="border border-secondary-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
            {leadsLoading ? (
              <div className="flex items-center justify-center py-10 text-secondary-400 text-sm">
                Carregando leads...
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-secondary-400">
                <Users className="w-8 h-8" />
                <p className="text-sm">Nenhum lead com e-mail encontrado</p>
              </div>
            ) : (
              <ul className="divide-y divide-secondary-100">
                {leads.map((lead) => {
                  const id = lead.id as number;
                  const isChecked = selectedLeads.includes(id);

                  return (
                    <li
                      key={id}
                      onClick={() => toggleLead(id)}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isChecked ? 'bg-primary-50' : 'hover:bg-secondary-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleLead(id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-secondary-300 text-primary-600 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">
                          {(lead.name as string) || '—'}
                        </p>
                        <p className="text-xs text-secondary-500 truncate">
                          {lead.email as string}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RecipientSelector;
